import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { Sport } from '__generated__/globalTypes';
import { useSportContext } from 'contexts/sport';
import { useQuery } from 'hooks/graphql/useQuery';
import { algoliaCardObjectIdPrefix, joinFiltersWithAnd } from 'lib/algolia';

import {
  BaseballCardsInLineupQuery,
  BaseballCardsInLineupQueryVariables,
  FootballCardsInLineupQuery,
  FootballCardsInLineupQueryVariables,
  NBACardsInLineupQuery,
  NBACardsInLineupQueryVariables,
  So5ContendersFragment_So5LeaderboardContender,
} from './__generated__/useNotInLineupFilterValue.graphql';

const FOOTBALL_QUERY = gql`
  query FootballCardsInLineupQuery {
    currentUser {
      slug
      blockchainCardsInLineups
    }
  }
` as TypedDocumentNode<
  FootballCardsInLineupQuery,
  FootballCardsInLineupQueryVariables
>;

const So5ContendersFragment = gql`
  fragment So5ContendersFragment_So5LeaderboardContender on So5LeaderboardContender {
    slug
    so5Lineup {
      id
      so5Appearances {
        id
        anyCard {
          slug
          assetId
        }
      }
    }
  }
` as TypedDocumentNode<So5ContendersFragment_So5LeaderboardContender>;

const BASEBALL_QUERY = gql`
  query BaseballCardsInLineupQuery {
    so5 {
      baseballOpenFixture: so5Fixture(sport: BASEBALL, type: UPCOMING) {
        slug
        mySo5LeaderboardContenders {
          nodes {
            slug
            ...So5ContendersFragment_So5LeaderboardContender
          }
        }
      }
      baseballLiveFixture: so5Fixture(sport: BASEBALL, type: LIVE) {
        slug
        mySo5LeaderboardContenders {
          nodes {
            slug
            ...So5ContendersFragment_So5LeaderboardContender
          }
        }
      }
    }
  }
  ${So5ContendersFragment}
` as TypedDocumentNode<
  BaseballCardsInLineupQuery,
  BaseballCardsInLineupQueryVariables
>;

const NBA_QUERY = gql`
  query NBACardsInLineupQuery {
    so5 {
      nbaOpenFixture: so5Fixture(sport: NBA, type: UPCOMING) {
        slug
        mySo5LeaderboardContenders {
          nodes {
            slug
            ...So5ContendersFragment_So5LeaderboardContender
          }
        }
      }
      nbaLiveFixture: so5Fixture(sport: NBA, type: LIVE) {
        slug
        mySo5LeaderboardContenders {
          nodes {
            slug
            ...So5ContendersFragment_So5LeaderboardContender
          }
        }
      }
    }
  }
  ${So5ContendersFragment}
` as TypedDocumentNode<NBACardsInLineupQuery, NBACardsInLineupQueryVariables>;

const toFilter = (objectIDs: string[] | undefined | null) => {
  if (!objectIDs?.length) return undefined;

  return `(${joinFiltersWithAnd(
    objectIDs.map(objectID => `NOT objectID:"${objectID}"`)
  )}) AND NOT rarity:common`;
};

const getBlockchainObjectIDsInLineups = (
  sport: Sport,
  contenders?: So5ContendersFragment_So5LeaderboardContender[]
) => {
  const appearances = contenders?.flatMap(contender => {
    return contender.so5Lineup?.so5Appearances;
  });
  return appearances
    ?.flatMap(
      appearance =>
        appearance?.anyCard?.assetId &&
        `${algoliaCardObjectIdPrefix[sport]}:${appearance.anyCard.assetId}`
    )
    .filter(Boolean);
};

export const useNotInLineupFilterValue = ({ skip }: { skip: boolean }) => {
  const { sport } = useSportContext();
  const { data: footballData } = useQuery(FOOTBALL_QUERY, {
    skip: skip || sport !== Sport.FOOTBALL,
  });
  const { data: baseballData } = useQuery(BASEBALL_QUERY, {
    skip: skip || sport !== Sport.BASEBALL,
  });
  const { data: nbaData } = useQuery(NBA_QUERY, {
    skip: skip || sport !== Sport.NBA,
  });

  const notInLineupFilterValue = useMemo(() => {
    switch (sport) {
      case Sport.BASEBALL:
        return toFilter(
          getBlockchainObjectIDsInLineups(Sport.BASEBALL, [
            ...(baseballData?.so5?.baseballOpenFixture
              ?.mySo5LeaderboardContenders?.nodes || []),
            ...(baseballData?.so5?.baseballLiveFixture
              ?.mySo5LeaderboardContenders?.nodes || []),
          ])
        );
      case Sport.NBA:
        return toFilter(
          getBlockchainObjectIDsInLineups(Sport.NBA, [
            ...(nbaData?.so5?.nbaOpenFixture?.mySo5LeaderboardContenders
              ?.nodes || []),
            ...(nbaData?.so5?.nbaLiveFixture?.mySo5LeaderboardContenders
              ?.nodes || []),
          ])
        );
      default:
        return toFilter(footballData?.currentUser?.blockchainCardsInLineups);
    }
  }, [footballData, baseballData, nbaData, sport]);

  return notInLineupFilterValue;
};
